import React, { useEffect, useState, useCallback } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  ReactFlowProvider,
  ReactFlowProps,
  Panel,
  useReactFlow
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useTheme } from '../../contexts/ThemeContext';
import { GraphToolbar } from './GraphToolbar';

interface BaseGraphCanvasProps extends ReactFlowProps {
  showGraphToolbar?: boolean;
  graphToolbarLayout?: 'compact' | 'full' | 'minimal';
  graphToolbarPosition?: 'top' | 'bottom' | 'left' | 'right';
  onLayoutChange?: (layout: string) => void;
  onConnectorTypeChange?: (type: string) => void;
  onExportImage?: () => void;
  onExportData?: () => void;
  onFocusNode?: (nodeId: string) => void;
  // Bulk operations
  onBulkAlignLeft?: () => void;
  onBulkAlignTop?: () => void;
  onBulkDistributeHorizontally?: () => void;
  onBulkDistributeVertically?: () => void;
  onBulkDelete?: () => void;
}

// The actual component that contains the ReactFlow instance
const BaseGraphCanvasComponent: React.FC<BaseGraphCanvasProps> = ({ 
  children, 
  nodes, 
  edges, 
  showGraphToolbar = false,
  graphToolbarLayout = 'compact',
  graphToolbarPosition = 'top',
  onLayoutChange,
  onConnectorTypeChange,
  onExportImage,
  onExportData,
  onFocusNode,
  onBulkAlignLeft,
  onBulkAlignTop,
  onBulkDistributeHorizontally,
  onBulkDistributeVertically,
  onBulkDelete,
  onNodesChange,
  onEdgesChange,
  ...props 
}) => {
  const { darkMode } = useTheme();
  const { fitView } = useReactFlow();

  // Graph toolbar state
  const [layoutMode, setLayoutMode] = useState<'default' | 'hierarchical' | 'circular' | 'force' | 'grid'>('default');
  const [connectorType, setConnectorType] = useState<'curved' | 'straight' | 'smoothstep' | 'step'>('curved');
  const [showMinimap, setShowMinimap] = useState(true);
  const [showBackground, setShowBackground] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [selectionMode, setSelectionMode] = useState<'single' | 'multi' | 'box' | 'lasso'>('single');
  const [showMetrics, setShowMetrics] = useState(false);
  const [showCriticalPath, setShowCriticalPath] = useState(false);
  const [showClusters, setShowClusters] = useState(false);
  const [animationSpeed, setAnimationSpeed] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);

  // Selection state
  const [selectedNodes, setSelectedNodes] = useState<string[]>([]);
  const [selectedEdges, setSelectedEdges] = useState<string[]>([]);
  const [isSelectionMode, setIsSelectionMode] = useState(false);

  // Handle layout changes
  const handleLayoutModeChange = (mode: 'default' | 'hierarchical' | 'circular' | 'force' | 'grid') => {
    setLayoutMode(mode);
    if (onLayoutChange) {
      onLayoutChange(mode);
    }
  };

  // Handle connector type changes
  const handleConnectorTypeChange = (type: 'curved' | 'straight' | 'smoothstep' | 'step') => {
    setConnectorType(type);
    if (onConnectorTypeChange) {
      onConnectorTypeChange(type);
    }
  };

  // Update selection handlers
  const handleClearSelection = useCallback(() => {
    setSelectedNodes([]);
    setSelectedEdges([]);
    // Clear selection in ReactFlow
    if (onNodesChange) {
      const changes = nodes?.map(node => ({
        id: node.id,
        type: 'select' as const,
        selected: false
      })) || [];
      onNodesChange(changes);
    }
    if (onEdgesChange) {
      const changes = edges?.map(edge => ({
        id: edge.id,
        type: 'select' as const,
        selected: false
      })) || [];
      onEdgesChange(changes);
    }
  }, [nodes, edges, onNodesChange, onEdgesChange]);

  const handleSelectAll = useCallback(() => {
    const nodeIds = nodes?.map(node => node.id) || [];
    const edgeIds = edges?.map(edge => edge.id) || [];
    
    setSelectedNodes(nodeIds);
    setSelectedEdges(edgeIds);
    
    // Select all in ReactFlow
    if (onNodesChange) {
      const changes = nodes?.map(node => ({
        id: node.id,
        type: 'select' as const,
        selected: true
      })) || [];
      onNodesChange(changes);
    }
    if (onEdgesChange) {
      const changes = edges?.map(edge => ({
        id: edge.id,
        type: 'select' as const,
        selected: true
      })) || [];
      onEdgesChange(changes);
    }
  }, [nodes, edges, onNodesChange, onEdgesChange]);

  const handleInvertSelection = useCallback(() => {
    const nodeIds = nodes?.filter(node => !selectedNodes.includes(node.id)).map(node => node.id) || [];
    const edgeIds = edges?.filter(edge => !selectedEdges.includes(edge.id)).map(edge => edge.id) || [];
    
    setSelectedNodes(nodeIds);
    setSelectedEdges(edgeIds);
    
    // Update selection in ReactFlow
    if (onNodesChange) {
      const changes = nodes?.map(node => ({
        id: node.id,
        type: 'select' as const,
        selected: !selectedNodes.includes(node.id)
      })) || [];
      onNodesChange(changes);
    }
    if (onEdgesChange) {
      const changes = edges?.map(edge => ({
        id: edge.id,
        type: 'select' as const,
        selected: !selectedEdges.includes(edge.id)
      })) || [];
      onEdgesChange(changes);
    }
  }, [nodes, edges, selectedNodes, selectedEdges, onNodesChange, onEdgesChange]);

  // Handle selection changes from ReactFlow
  const handleSelectionChange = useCallback(({ nodes: selectedNodes, edges: selectedEdges }) => {
    const nodeIds = selectedNodes.map(node => node.id);
    const edgeIds = selectedEdges.map(edge => edge.id);
    
    console.log('🔍 BaseGraphCanvas selection change:', { nodeIds, edgeIds }); // Debug log
    
    setSelectedNodes(nodeIds);
    setSelectedEdges(edgeIds);
    
    // Also pass the selection change up to the parent
    if (props.onSelectionChange) {
      props.onSelectionChange({ nodes: selectedNodes, edges: selectedEdges });
    }
  }, [props.onSelectionChange]);

  // Calculate selected count from ReactFlow's native selection
  const selectedCount = (nodes?.filter(node => node.selected).length || 0) + 
                       (edges?.filter(edge => edge.selected).length || 0);

  // Add debug logging
  console.log('🔍 BaseGraphCanvas selectedCount:', selectedCount);
  console.log('🔍 Selected nodes:', nodes?.filter(node => node.selected).map(n => n.id));
  console.log('🔍 onBulkAlignLeft exists:', !!onBulkAlignLeft);

  // Export handlers
  const handleExportImage = () => {
    if (onExportImage) {
      onExportImage();
    } else {
      // Default export implementation
      console.log('Export image functionality not implemented');
    }
  };

  const handleExportData = () => {
    if (onExportData) {
      onExportData();
    } else {
      // Default export implementation
      console.log('Export data functionality not implemented');
    }
  };

  // Add selection box styling
  const selectionBoxStyle = {
    background: darkMode 
      ? 'rgba(59, 130, 246, 0.1)' 
      : 'rgba(59, 130, 246, 0.1)',
    border: darkMode 
      ? '2px solid rgba(59, 130, 246, 0.6)' 
      : '2px solid rgba(59, 130, 246, 0.6)',
    borderRadius: '4px',
    boxShadow: darkMode
      ? '0 0 20px rgba(59, 130, 246, 0.4)'
      : '0 0 20px rgba(59, 130, 246, 0.3)'
  };

  // Enhanced ReactFlow props for selection
  const reactFlowProps = {
    ...props,
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onSelectionChange: handleSelectionChange,
    // Enhanced selection styling
    selectNodesOnDrag: false,
    multiSelectionKeyCode: ['Control', 'Meta'], // Support both Ctrl and Cmd
    panOnDrag: [1, 2], // Pan with left and middle mouse buttons
    selectionOnDrag: true,
    selectionMode: 'partial', // Allow partial selection
    // Custom selection box style
    selectionBoxStyle,
    nodesDraggable: true,
    nodesConnectable: true,
  };

  // Add focus node handler
  const handleFocusNode = (nodeId: string) => {
    if (onFocusNode) {
      onFocusNode(nodeId);
    } else {
      // Default behavior - center on node
      const node = nodes?.find(n => n.id === nodeId);
      if (node) {
        fitView({ 
          nodes: [{ id: nodeId }],
          duration: 800,
          padding: 0.3
        });
      }
    }
  };

  // Add debugging to see if nodes and edges are being passed correctly
  useEffect(() => {
    console.log('BaseGraphCanvas: Rendering with nodes:', nodes?.length);
    console.log('BaseGraphCanvas: Rendering with edges:', edges?.length);
  }, [nodes, edges]);

  return (
    <div style={{ height: '100%', width: '100%' }} className={darkMode ? 'dark-mode' : ''}>
      <ReactFlow {...reactFlowProps}>
        {showBackground && <Background color={darkMode ? '#4B5563' : '#E5E7EB'} gap={16} />}
        
        {/* Only show default Controls if GraphToolbar is not being used */}
        {showControls && !showGraphToolbar && <Controls />}
        
        {showMinimap && (
          <MiniMap 
            nodeStrokeColor={darkMode ? '#1F2937' : '#FFFFFF'}
            nodeColor={darkMode ? '#6B7280' : '#9CA3AF'}
            nodeStrokeWidth={1}
            maskColor={darkMode ? 'rgba(17, 24, 39, 0.5)' : 'rgba(255, 255, 255, 0.5)'}
            className={darkMode ? 'dark-mode-minimap' : ''}
            showEdges={false}
            style={{
              borderRadius: '6px',
              border: darkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
              boxShadow: darkMode 
                ? '0 2px 4px -1px rgba(0, 0, 0, 0.3), 0 1px 2px -1px rgba(0, 0, 0, 0.2)'
                : '0 2px 4px -1px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.06)',
              width: 150,
              height: 100,
              backgroundColor: darkMode 
                ? 'rgba(31, 41, 55, 0.8)'
                : 'rgba(255, 255, 255, 0.8)',
              zIndex: 5
            }}
            pannable
            zoomable
          />
        )}

        {/* Graph Toolbar */}
        {showGraphToolbar && (
          <Panel 
            position={graphToolbarPosition === 'top' ? 'top-center' : 
                     graphToolbarPosition === 'bottom' ? 'bottom-center' :
                     graphToolbarPosition === 'left' ? 'top-left' : 'top-right'}
            className="z-10"
          >
            <GraphToolbar
              layoutMode={layoutMode}
              onLayoutModeChange={handleLayoutModeChange}
              connectorType={connectorType}
              onConnectorTypeChange={handleConnectorTypeChange}
              showMinimap={showMinimap}
              onShowMinimapChange={setShowMinimap}
              showBackground={showBackground}
              onShowBackgroundChange={setShowBackground}
              showControls={showControls}
              onShowControlsChange={setShowControls}
              selectionMode={selectionMode}
              onSelectionModeChange={setSelectionMode}
              selectedCount={selectedCount}
              onClearSelection={handleClearSelection}
              onSelectAll={handleSelectAll}
              onInvertSelection={handleInvertSelection}
              onBulkAlignLeft={onBulkAlignLeft}
              onBulkAlignTop={onBulkAlignTop}
              onBulkDistributeHorizontally={onBulkDistributeHorizontally}
              onBulkDistributeVertically={onBulkDistributeVertically}
              onBulkDelete={onBulkDelete}
              showMetrics={showMetrics}
              onShowMetricsChange={setShowMetrics}
              showCriticalPath={showCriticalPath}
              onShowCriticalPathChange={setShowCriticalPath}
              showClusters={showClusters}
              onShowClustersChange={setShowClusters}
              onExportImage={handleExportImage}
              onExportData={handleExportData}
              onImportData={() => console.log('Import data')}
              animationSpeed={animationSpeed}
              onAnimationSpeedChange={setAnimationSpeed}
              isAnimating={isAnimating}
              onToggleAnimation={() => setIsAnimating(!isAnimating)}
              layout={graphToolbarLayout}
              position={graphToolbarPosition}
              nodes={nodes}
              onFocusNode={handleFocusNode}
              onNodesChange={onNodesChange}
            />
          </Panel>
        )}

        {children}
      </ReactFlow>
    </div>
  );
};

// The exported component wraps everything in the necessary provider
export const BaseGraphCanvas: React.FC<BaseGraphCanvasProps> = (props) => (
  <ReactFlowProvider>
    <BaseGraphCanvasComponent {...props} />
  </ReactFlowProvider>
);
